import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, Spinner } from '@structural-climbing/ui'
import { competitionBoards, type CompetitionDiscipline } from '@structural-climbing/validation'
import { RouteDetailSheet } from '../routes/RouteDetailSheet'
import type { GymRoute } from '../routes/useGymRoutes'
import { routeBoard } from './boards'
import { CompetitionRouteRow } from './CompetitionRouteRow'
import type { CompetitionRoute } from './types'
import { useCompetitionRoutes } from './useCompetitionRoutes'

// The live round is the only one that can be acted on, so it leads; what is
// still to come reads better than what is already over.
const ROUND_RANK: Record<string, number> = { in_progress: 0, upcoming: 1, ended: 2 }

interface Props {
  competitionId: string
  gymId: string
  discipline: CompetitionDiscipline
  /**
   * How the competition is scored. Under `tops_count` a top is a top, so the
   * sheet stops asking how many attempts it took.
   */
  scoringType?: string
  userId: string
  apiUrl: string
  canLog: boolean
  /** Why logging is closed, in this competition's own terms. */
  cannotLogReason?: string
  getAccessTokenSilently: () => Promise<string>
  onLogged: () => void
  /** The server refused a log for a reason this page can show, e.g. the group ended. */
  onRefused?: () => void
}

export function CompetitionRoutesTab({
  competitionId,
  gymId,
  discipline,
  scoringType,
  userId,
  apiUrl,
  canLog,
  cannotLogReason,
  getAccessTokenSilently,
  onLogged,
  onRefused,
}: Props) {
  const { t } = useTranslation()
  const { routes, rounds, loading, applyAscent, removeAscent } = useCompetitionRoutes(
    competitionId,
    apiUrl,
    getAccessTokenSilently,
  )
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  // A combined competition lists bouldering, then lead, each split by round
  // the same way a single-discipline one is.
  const boards = useMemo(() => {
    function groupByRound(list: CompetitionRoute[]) {
      if (rounds.length === 0) return [{ key: 'all', name: '', state: null, routes: list }]
      const byRound = [...rounds]
        .sort((a, b) => ROUND_RANK[a.state] - ROUND_RANK[b.state] || a.sort_order - b.sort_order)
        .map((round) => ({
          key: round.id,
          name: round.name,
          state: round.state as string | null,
          routes: list.filter((r) => r.round?.id === round.id),
        }))
      // Routes outside any round are live for the whole competition, so they
      // sit after the rounds rather than inside one.
      const loose = list.filter((r) => r.round === null)
      return loose.length
        ? [
            ...byRound,
            {
              key: 'all',
              name: t('competitions.roundsAllCompetition'),
              state: null,
              routes: loose,
            },
          ]
        : byRound
    }

    const all = competitionBoards(discipline)
    return all.map((board) => {
      const boardRoutes =
        all.length > 1
          ? routes.filter((r) => routeBoard(discipline, r.section?.discipline) === board)
          : routes
      return { board, heading: all.length > 1, groups: groupByRound(boardRoutes) }
    })
  }, [routes, rounds, discipline, t])

  // Flattened in display order, so the sheet's prev/next walks the list the way
  // it is read rather than the order the server happened to return.
  const ordered = useMemo(() => boards.flatMap((b) => b.groups.flatMap((g) => g.routes)), [boards])

  const selected = selectedIndex === null ? null : (ordered[selectedIndex] ?? null)

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    )
  }

  if (routes.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardBody className="py-10 text-center text-foreground/60">
          {t('competitions.routesEmpty')}
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Competition logs and gym logs are independent, which is not obvious. */}
      <p className="rounded-lg bg-content2 p-3 text-xs text-foreground/60">
        {t('competitions.notCountedNote')}
      </p>

      {boards.map(({ board, heading, groups }) => (
        <div key={board} className="flex flex-col gap-3">
          {heading && groups.some((g) => g.routes.length > 0) && (
            <h2 className="px-1 font-bold">
              {t(board === 'lead' ? 'routes.disciplineLead' : 'routes.disciplineBouldering')}
            </h2>
          )}
          {groups
            .filter((group) => group.routes.length > 0)
            .map((group) => (
              <div key={group.key} className="flex flex-col gap-2">
                {group.name && (
                  <h3 className="flex flex-wrap items-center gap-2 px-1 text-sm font-semibold">
                    {group.name}
                    {group.state && (
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none ${
                          group.state === 'in_progress'
                            ? 'bg-success/15 text-success-700'
                            : 'bg-default-200 text-foreground/60'
                        }`}
                      >
                        {t(
                          group.state === 'in_progress'
                            ? 'competitions.roundLive'
                            : group.state === 'upcoming'
                              ? 'competitions.roundUpcoming'
                              : 'competitions.roundClosed',
                        )}
                      </span>
                    )}
                  </h3>
                )}
                {group.routes.map((route) => (
                  <CompetitionRouteRow
                    key={route.id}
                    route={route}
                    apiUrl={apiUrl}
                    discipline={routeBoard(discipline, route.section?.discipline)}
                    onClick={() =>
                      !route.removed &&
                      !route.closed &&
                      setSelectedIndex(ordered.findIndex((r) => r.id === route.id))
                    }
                  />
                ))}
              </div>
            ))}
        </div>
      ))}

      <RouteDetailSheet
        route={selected as unknown as GymRoute | null}
        gymId={gymId}
        userId={userId}
        apiUrl={apiUrl}
        discipline={routeBoard(discipline, selected?.section?.discipline)}
        getAccessTokenSilently={getAccessTokenSilently}
        canLogAscent={canLog}
        cannotLogReason={cannotLogReason}
        // The differences that make this a competition sheet. Ratings and
        // notes to the setters are off because a competition never collects
        // either: they live on `ascents` and `route_comments`, and these
        // endpoints write `competition_ascents`. Repeats are off because a
        // competition keeps one ascent per route.
        showClimbers={false}
        showRating={false}
        askComment={false}
        allowRepeat={false}
        askAttempts={scoringType !== 'tops_count'}
        ascentEndpoints={{
          create: `${apiUrl}/my-competitions/${competitionId}/ascents`,
          item: (routeId: string) =>
            `${apiUrl}/my-competitions/${competitionId}/ascents/${routeId}`,
        }}
        // What requireOpenEntry refuses with, said in the competition's terms
        // rather than as a generic "could not save".
        saveErrorMessages={{
          SESSION_CLOSED: t('competitions.errorGroupClosed'),
          COMPETITION_ENDED: t('competitions.errorCompetitionEnded'),
          ENTRY_NOT_CONFIRMED: t('competitions.cannotLogAwaiting'),
        }}
        onSaveRefused={onRefused}
        onClose={() => setSelectedIndex(null)}
        onLogAscent={(routeId, attempts) => {
          applyAscent(routeId, attempts)
          onLogged()
        }}
        onRemoveAscent={(routeId) => {
          removeAscent(routeId)
          onLogged()
        }}
        onUpdateAscent={(routeId, attempts) => {
          applyAscent(routeId, attempts)
          onLogged()
        }}
        hasPrev={selectedIndex !== null && selectedIndex > 0}
        hasNext={selectedIndex !== null && selectedIndex < ordered.length - 1}
        nextLoading={false}
        onPrev={() => setSelectedIndex((i) => (i === null ? null : Math.max(0, i - 1)))}
        onNext={() =>
          setSelectedIndex((i) => (i === null ? null : Math.min(ordered.length - 1, i + 1)))
        }
      />
    </div>
  )
}
